import React, { useEffect, useState } from 'react';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Modal } from '../components/ui/Modal';
import { Pagination } from '../components/ui/Pagination';
import { Textarea } from '../components/ui/Textarea';
import { DatePicker } from '../components/ui/DatePicker';
import { useSearch } from '../hooks/useSearch';
import { useFilters } from '../hooks/useFilters';
import { usePagination } from '../hooks/usePagination';
import { ColumnCustomizer } from '../components/ColumnCustomizer';
import { ReportModal } from '../components/operations/ReportModal';
import { Plus, Filter, Download, Eye, Edit, Search, MapPin, FileText, X, Save, CheckCircle2, Calendar, Mail, Briefcase } from 'lucide-react';
import { useNotifications } from '../contexts/NotificationContext';
import { motion, AnimatePresence } from 'framer-motion';
// Activity types 1-7
const activityTypes = [{
  value: '1',
  label: 'Tipo 1 - Sopralluogo'
}, {
  value: '2',
  label: 'Tipo 2 - Consegna DVR'
}, {
  value: '3',
  label: 'Tipo 3 - Campionamento'
}, {
  value: '4',
  label: 'Tipo 4 - Formazione'
}, {
  value: '5',
  label: 'Tipo 5 - Audit'
}, {
  value: '6',
  label: 'Tipo 6 - Consulenza'
}, {
  value: '7',
  label: 'Tipo 7 - Altro'
}];
// Status options with sub-states
const statusOptions = [{
  value: 'da_pianificare',
  label: 'Da pianificare'
}, {
  value: 'pianificato',
  label: 'Pianificato (mese)'
}, {
  value: 'calendarizzato',
  label: 'Calendarizzato (data)'
}, {
  value: 'in_svolgimento_attivo',
  label: 'In svolgimento - Attivo'
}, {
  value: 'in_svolgimento_sospeso',
  label: 'In svolgimento - Sospeso'
}, {
  value: 'in_svolgimento_stop',
  label: 'In svolgimento - Stop'
}, {
  value: 'completo',
  label: 'Completo'
}];
const statusLabels: Record<string, string> = {
  da_pianificare: 'Da pianificare',
  pianificato: 'Pianificato',
  calendarizzato: 'Calendarizzato',
  in_svolgimento_attivo: 'Attivo',
  in_svolgimento_sospeso: 'Sospeso',
  in_svolgimento_stop: 'Stop',
  completo: 'Completo',
  // Legacy
  'Da pianificare': 'Da pianificare',
  Attivo: 'Attivo',
  Sospeso: 'Sospeso'
};
const statusColors: Record<string, string> = {
  da_pianificare: 'neutral',
  pianificato: 'info',
  calendarizzato: 'primary',
  in_svolgimento_attivo: 'success',
  in_svolgimento_sospeso: 'warning',
  in_svolgimento_stop: 'danger',
  completo: 'success',
  'Da pianificare': 'neutral',
  Attivo: 'success',
  Sospeso: 'warning'
};
// Mock data
const initialOperationsData = Array.from({
  length: 50
}, (_, i) => ({
  id: i + 1,
  code: `ATT-2025/${String(i + 1).padStart(3, '0')}`,
  activity: i % 3 === 0 ? 'Sopralluogo Periodico' : i % 3 === 1 ? 'Consegna DVR' : 'Campionamento',
  activityType: String(i % 7 + 1),
  client: `Azienda ${i} SRL`,
  contract: `C-2024/${String(i + 1).padStart(3, '0')}`,
  operator: i % 2 === 0 ? 'Alessio' : 'Lorenzo',
  status: i % 7 === 0 ? 'da_pianificare' : i % 7 === 1 ? 'pianificato' : i % 7 === 2 ? 'calendarizzato' : i % 7 === 3 ? 'in_svolgimento_attivo' : i % 7 === 4 ? 'in_svolgimento_sospeso' : i % 7 === 5 ? 'in_svolgimento_stop' : 'completo',
  deadline: '30/05/2024',
  scheduledDate: i % 7 === 2 ? '2024-05-15' : '',
  scheduledMonth: i % 7 === 1 ? '2024-05' : '',
  zone: ['Arezzo', 'Valdarno', 'Casentino'][i % 3]
}));
const columnsConfig = [{
  key: 'code',
  label: 'Codice'
}, {
  key: 'activity',
  label: 'Attività'
}, {
  key: 'client',
  label: 'Cliente'
}, {
  key: 'contract',
  label: 'Contratto'
}, {
  key: 'operator',
  label: 'Operatore'
}, {
  key: 'zone',
  label: 'Zona'
}, {
  key: 'status',
  label: 'Stato'
}, {
  key: 'deadline',
  label: 'Scadenza'
}, {
  key: 'actions',
  label: 'Azioni'
}];
export function OperationsPage() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isReportOpen, setIsReportOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(columnsConfig.map(c => c.key));
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [formData, setFormData] = useState<any>({});
  const {
    addNotification
  } = useNotifications();
  const {
    filters,
    setFilter,
    clearFilters,
    filteredData: filterResult
  } = useFilters(initialOperationsData);
  const {
    searchTerm,
    setSearchTerm,
    filteredData: searchResult
  } = useSearch(filterResult, ['activity', 'client', 'code', 'contract']);
  const {
    currentPage,
    totalPages,
    paginatedData,
    goToPage,
    pageSize,
    setPageSize,
    totalItems,
    setCurrentPage
  } = usePagination(searchResult);
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, searchTerm]);
  const handleEdit = (item: any) => {
    setSelectedItem(item);
    setFormData(item);
    setIsModalOpen(true);
  };
  const handleCreate = () => {
    setSelectedItem(null);
    // Pre-fill code
    const nextCode = `ATT-2025/${String(initialOperationsData.length + 1).padStart(3, '0')}`;
    setFormData({
      code: nextCode,
      status: 'da_pianificare',
      sendEmail: false
    });
    setIsModalOpen(true);
  };
  const handleReport = (item: any) => {
    setSelectedItem(item);
    setIsReportOpen(true);
  };
  const handleSave = () => {
    setSaveSuccess(true);
    setTimeout(() => {
      setSaveSuccess(false);
      setIsModalOpen(false);
      addNotification('success', 'Attività salvata', "L'attività è stata salvata correttamente.");
    }, 1500);
  };
  const hasActiveFilters = Object.keys(filters).some(k => filters[k]);
  // Check if status requires date or month
  const showScheduledDate = formData.status === 'calendarizzato';
  const showScheduledMonth = formData.status === 'pianificato';
  const showSubStatus = formData.status?.startsWith('in_svolgimento');
  return <Layout data-id="element-2532">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4" data-id="element-2533">
        <div data-id="element-2534">
          <h1 className="text-3xl font-bold text-seform-navy" data-id="element-2535">
            Attività operative
          </h1>
          <p className="text-slate-500 mt-1" data-id="element-2536">
            Pianificazione, reportistica e audit delle attività.
          </p>
        </div>
        <div className="flex gap-3" data-id="element-2537">
          <Button variant="flat" color="secondary" startContent={<Download className="h-4 w-4" data-id="element-2539" />} data-id="element-2538">
            Esporta
          </Button>
          <Button onClick={handleCreate} startContent={<Plus className="h-4 w-4" data-id="element-2541" />} data-id="element-2540">
            Nuova attività
          </Button>
        </div>
      </div>

      <Card className="mb-6" noPadding data-id="element-2542">
        <div className="p-4 flex flex-col gap-4" data-id="element-2543">
          <div className="flex flex-col sm:flex-row gap-4" data-id="element-2544">
            <div className="flex-1" data-id="element-2545">
              <Input placeholder="Cerca attività, cliente, contratto..." startContent={<Search className="h-4 w-4" data-id="element-2547" />} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} fullWidth data-id="element-2546" />
            </div>
            <div className="flex gap-2" data-id="element-2548">
              <Button variant={showFilters ? 'solid' : 'flat'} color="secondary" onClick={() => setShowFilters(!showFilters)} startContent={<Filter className="h-4 w-4" data-id="element-2550" />} data-id="element-2549">
                Filtri{' '}
                {hasActiveFilters && <Badge variant="primary" size="sm" className="ml-1" data-id="element-2551">
                    {Object.keys(filters).filter(k => filters[k]).length}
                  </Badge>}
              </Button>
              <ColumnCustomizer columns={columnsConfig} visibleColumns={visibleColumns} onColumnChange={setVisibleColumns} pageKey="operations" data-id="element-2552" />
            </div>
          </div>

          {showFilters && <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-slate-100 animate-in fade-in slide-in-from-top-2" data-id="element-2553">
              <Select label="Stato" value={filters.status || ''} onChange={e => setFilter('status', e.target.value)} options={[{
            value: '',
            label: 'Tutti'
          }, ...statusOptions]} data-id="element-2554" />
              <Select label="Operatore" value={filters.operator || ''} onChange={e => setFilter('operator', e.target.value)} options={[{
            value: '',
            label: 'Tutti'
          }, {
            value: 'Alessio',
            label: 'Alessio'
          }, {
            value: 'Lorenzo',
            label: 'Lorenzo'
          }, {
            value: 'Gian Franco',
            label: 'Gian Franco'
          }]} data-id="element-2555" />
              <Select label="Zona" value={filters.zone || ''} onChange={e => setFilter('zone', e.target.value)} options={[{
            value: '',
            label: 'Tutte'
          }, {
            value: 'Arezzo',
            label: 'Arezzo'
          }, {
            value: 'Valdarno',
            label: 'Valdarno'
          }, {
            value: 'Casentino',
            label: 'Casentino'
          }]} data-id="element-2556" />
              <div className="col-span-full flex justify-end" data-id="element-2557">
                <Button variant="flat" color="danger" size="sm" onClick={clearFilters} startContent={<X className="h-4 w-4" data-id="element-2559" />} data-id="element-2558">
                  Resetta filtri
                </Button>
              </div>
            </div>}
        </div>
      </Card>

      <Card noPadding className="overflow-hidden" data-id="element-2560">
        <div className="overflow-x-auto" data-id="element-2561">
          <table className="w-full text-left border-collapse table-fixed" data-id="element-2562">
            <thead data-id="element-2563">
              <tr className="bg-slate-50/50 border-b border-slate-100 text-xs uppercase tracking-wider text-slate-500 font-semibold" data-id="element-2564">
                {columnsConfig.filter(c => visibleColumns.includes(c.key)).map(col => <th key={col.key} className={`px-6 py-4 ${col.key === 'actions' ? 'text-right w-28' : ''}`} data-id="element-2565">
                      {col.label}
                    </th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-2566">
              {paginatedData.map(item => <tr key={item.id} className="hover:bg-slate-50/80 transition-colors group" data-id="element-2567">
                  {visibleColumns.includes('code') && <td className="px-6 py-4 font-mono text-xs text-slate-500" data-id="element-2568">
                      {item.code}
                    </td>}
                  {visibleColumns.includes('activity') && <td className="px-6 py-4 font-medium text-slate-900" data-id="element-2569">
                      {item.activity}
                    </td>}
                  {visibleColumns.includes('client') && <td className="px-6 py-4 text-sm text-slate-600" data-id="element-2570">
                      {item.client}
                    </td>}
                  {visibleColumns.includes('contract') && <td className="px-6 py-4 font-mono text-xs text-slate-500" data-id="element-2571">
                      {item.contract}
                    </td>}
                  {visibleColumns.includes('operator') && <td className="px-6 py-4 text-sm text-slate-600" data-id="element-2572">
                      {item.operator}
                    </td>}
                  {visibleColumns.includes('zone') && <td className="px-6 py-4" data-id="element-2573">
                      <div className="flex items-center gap-1.5 text-sm text-slate-600" data-id="element-2574">
                        <MapPin className="h-3.5 w-3.5 text-slate-400" data-id="element-2575" />
                        {item.zone}
                      </div>
                    </td>}
                  {visibleColumns.includes('status') && <td className="px-6 py-4" data-id="element-2576">
                      <Badge variant={statusColors[item.status] as any || 'neutral'} data-id="element-2577">
                        {statusLabels[item.status] || item.status}
                      </Badge>
                    </td>}
                  {visibleColumns.includes('deadline') && <td className="px-6 py-4 text-sm text-slate-600" data-id="element-2578">
                      {item.deadline}
                    </td>}
                  {visibleColumns.includes('actions') && <td className="px-6 py-4 text-right" data-id="element-2579">
                      <div className="flex items-center justify-end gap-2" data-id="element-2580">
                        <Button variant="light" size="sm" className="!px-2" onClick={() => handleReport(item)} data-id="element-2581">
                          <FileText className="h-4 w-4 text-blue-500" data-id="element-2582" />
                        </Button>
                        <Button variant="light" size="sm" className="!px-2" onClick={() => handleEdit(item)} data-id="element-2583">
                          <Edit className="h-4 w-4 text-seform-orange" data-id="element-2584" />
                        </Button>
                      </div>
                    </td>}
                </tr>)}
            </tbody>
          </table>
        </div>
        <div className="border-t border-slate-100" data-id="element-2585">
          <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={goToPage} totalItems={totalItems} pageSize={pageSize} onPageSizeChange={setPageSize} data-id="element-2586" />
        </div>
      </Card>

      {/* Create/Edit Modal - Single modal, not wizard */}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={selectedItem ? 'Modifica attività' : 'Nuova attività'} size="2xl" data-id="element-2587">
        <AnimatePresence mode="wait" data-id="element-2588">
          {saveSuccess ? <motion.div key="success" initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="flex flex-col items-center justify-center py-16" data-id="element-2589">
              <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 200,
            damping: 15
          }} className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mb-4" data-id="element-2590">
                <CheckCircle2 className="w-12 h-12 text-green-500" data-id="element-2591" />
              </motion.div>
              <p className="text-lg font-semibold text-slate-800" data-id="element-2592">
                Attività salvata con successo
              </p>
            </motion.div> : <motion.div key="form" initial={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} data-id="element-2593">
              <div className="space-y-6" data-id="element-2594">
                {/* Main form - single modal */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2595">
                  <Select label="Contratto" value={formData.contract || ''} onChange={e => setFormData({
                ...formData,
                contract: e.target.value
              })} options={[{
                value: 'C-2024/001',
                label: 'C-2024/001 - Azienda 1 SRL'
              }, {
                value: 'C-2024/002',
                label: 'C-2024/002 - Azienda 2 SRL'
              }, {
                value: 'C-2024/003',
                label: 'C-2024/003 - Azienda 3 SRL'
              }]} required data-id="element-2596" />
                  <Select label="Cliente" value={formData.client || ''} onChange={e => setFormData({
                ...formData,
                client: e.target.value
              })} options={[{
                value: 'Azienda 1 SRL',
                label: 'Azienda 1 SRL'
              }, {
                value: 'Azienda 2 SRL',
                label: 'Azienda 2 SRL'
              }, {
                value: 'Azienda 3 SRL',
                label: 'Azienda 3 SRL'
              }]} required data-id="element-2597" />
                  <Input label="Codice attività" value={formData.code || ''} onChange={e => setFormData({
                ...formData,
                code: e.target.value
              })} placeholder="ATT-2025/001" data-id="element-2598" />
                  <Select label="Tipologia (1-7)" value={formData.activityType || ''} onChange={e => setFormData({
                ...formData,
                activityType: e.target.value
              })} options={activityTypes} required data-id="element-2599" />
                  <Input label="Nome attività" value={formData.activity || ''} onChange={e => setFormData({
                ...formData,
                activity: e.target.value
              })} placeholder="Es. Sopralluogo periodico" className="col-span-full" data-id="element-2600" />
                  <Select label="Operatore" value={formData.operator || ''} onChange={e => setFormData({
                ...formData,
                operator: e.target.value
              })} options={[{
                value: 'Alessio',
                label: 'Alessio'
              }, {
                value: 'Lorenzo',
                label: 'Lorenzo'
              }, {
                value: 'Gian Franco',
                label: 'Gian Franco'
              }, {
                value: 'Tiziano',
                label: 'Tiziano'
              }]} required data-id="element-2601" />
                  <Select label="Zona" value={formData.zone || ''} onChange={e => setFormData({
                ...formData,
                zone: e.target.value
              })} options={[{
                value: 'Arezzo',
                label: 'Arezzo'
              }, {
                value: 'Valdarno',
                label: 'Valdarno'
              }, {
                value: 'Casentino',
                label: 'Casentino'
              }, {
                value: 'Valdichiana',
                label: 'Valdichiana'
              }, {
                value: 'Firenze',
                label: 'Firenze'
              }]} data-id="element-2602" />
                </div>

                <div className="h-px bg-slate-100" data-id="element-2603"></div>

                {/* Status section */}
                <div className="space-y-4" data-id="element-2604">
                  <h4 className="text-sm font-bold text-seform-navy uppercase tracking-wider flex items-center gap-2" data-id="element-2605">
                    <Briefcase className="h-4 w-4" data-id="element-2606" /> Stato attività
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2607">
                    <Select label="Stato" value={formData.status || 'da_pianificare'} onChange={e => setFormData({
                  ...formData,
                  status: e.target.value
                })} options={statusOptions} required data-id="element-2608" />

                    {showScheduledMonth && <Input label="Mese pianificato" type="month" value={formData.scheduledMonth || ''} onChange={e => setFormData({
                  ...formData,
                  scheduledMonth: e.target.value
                })} data-id="element-2609" />}

                    {showScheduledDate && <>
                        <DatePicker label="Data calendarizzata" value={formData.scheduledDate || ''} onChange={e => setFormData({
                    ...formData,
                    scheduledDate: e.target.value
                  })} data-id="element-2610" />
                        <div className="flex items-center gap-2" data-id="element-2611">
                          <input type="checkbox" id="sendEmail" className="rounded text-seform-orange focus:ring-seform-orange" checked={formData.sendEmail || false} onChange={e => setFormData({
                      ...formData,
                      sendEmail: e.target.checked
                    })} data-id="element-2612" />
                          <label htmlFor="sendEmail" className="text-sm text-slate-700 flex items-center gap-1" data-id="element-2613">
                            <Mail className="h-4 w-4 text-slate-400" data-id="element-2614" />
                            Invia email di notifica
                          </label>
                        </div>
                      </>}

                    <DatePicker label="Scadenza" value={formData.deadline || ''} onChange={e => setFormData({
                  ...formData,
                  deadline: e.target.value
                })} data-id="element-2615" />
                  </div>
                </div>

                <div className="h-px bg-slate-100" data-id="element-2616"></div>

                {/* Notes */}
                <Textarea label="Note" value={formData.notes || ''} onChange={e => setFormData({
              ...formData,
              notes: e.target.value
            })} placeholder="Note aggiuntive sull'attività..." data-id="element-2617" />

                <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-2618">
                  <Button variant="flat" color="default" onClick={() => setIsModalOpen(false)} data-id="element-2619">
                    Annulla
                  </Button>
                  <Button onClick={handleSave} startContent={<Save className="h-4 w-4" data-id="element-2621" />} data-id="element-2620">
                    Salva attività
                  </Button>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </Modal>

      <ReportModal isOpen={isReportOpen} onClose={() => setIsReportOpen(false)} data-id="element-2622" />
    </Layout>;
}